import React from 'react';
import { useTranslation, withTranslation, Trans } from 'react-i18next';
import { Box, InfoIcon, Tooltip } from 'edenred-ui';
import { useTheme } from 'styled-components';

import { OrderSummaryProps } from '@epi-models/components/OrderSummary';

import { formatCurrency } from '../../helpers/numeral';
import { CardType } from '../../constants/cardType';

import './OrderSummary.scss';

const OrderSummary = ({
  benefitsSummary,
  cardOrderSummary
}: OrderSummaryProps) => {
  const { t } = useTranslation();
  const { gray25, primaryFrontColor } = useTheme();

  const taxableBaseValue =
    cardOrderSummary.duoCardsCosts +
    cardOrderSummary.transportCardsCosts +
    cardOrderSummary.transportCommissionAmount +
    cardOrderSummary.recreationalCommissionAmount +
    cardOrderSummary.lunchCommissionAmount;
  const balanceForBenefits =
    cardOrderSummary.orderTotalWithoutVat - taxableBaseValue;
  const cardType = CardType.plasticless;
  const showCardPrice =
    cardOrderSummary.duoCardsCosts > 0 && cardType !== CardType.plasticless;

  return (
    <Box display="flex" className="OrderSummary">
      <Box flex={1} pr={50}>
        <Box display="flex" flexDirection="column">
          <Box
            className="headers"
            display="flex"
            justifyContent="space-between"
            borderBottom={`1px solid ${gray25}`}
            py={1}
          >
            <Box>
              {t(
                'containers.card_order.summary_and_payment.order_summary.product'
              )}
            </Box>
            <Box>
              {t(
                'containers.card_order.summary_and_payment.order_summary.details'
              )}
            </Box>
            <Box>
              {t('containers.card_order.summary_and_payment.order_summary.sum')}
            </Box>
          </Box>
          {benefitsSummary.lunch.totalAmount > 0 && (
            <Box
              className="headers"
              display="flex"
              justifyContent="space-between"
              borderBottom={`1px solid ${gray25}`}
              py={1}
            >
              <Box>
                {t(
                  'containers.card_order.summary_and_payment.order_summary.lunch_benefit_balance'
                )}
              </Box>
              <Box>
                <Box>
                  <span>
                    {t(
                      'containers.card_order.summary_and_payment.order_summary.lunch_estimated_benefit_balance'
                    )}
                  </span>
                </Box>
                <Box>
                  {t(
                    'containers.card_order.summary_and_payment.order_summary.lunch_benefit_balance_details',
                    {
                      numberOfBeneficiaries:
                        benefitsSummary.lunch.beneficiaryNumber,
                      faceValue: formatCurrency(benefitsSummary.lunch.amount),
                      workdays: benefitsSummary.lunch.workdays
                    }
                  )}
                </Box>
                <Box>{formatCurrency(benefitsSummary.lunch.totalAmount)}</Box>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    {t(
                      'containers.card_order.summary_and_payment.order_summary.load_comission_for_lunch_benefit',
                      { vatPercentage: cardOrderSummary.vatPercentage }
                    )}
                  </Box>
                  <Box>
                    {formatCurrency(cardOrderSummary.lunchCommissionAmount)}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
          {benefitsSummary.recreational.totalAmount > 0 && (
            <Box
              className="body"
              display="flex"
              justifyContent="space-between"
              borderBottom={`1px solid ${gray25}`}
              py={1}
              flex={1}
            >
              <Box>
                {t(
                  'containers.card_order.summary_and_payment.order_summary.recreational_benefit_balance'
                )}
              </Box>
              <Box className="padding-left">
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    {t(
                      'containers.card_order.summary_and_payment.order_summary.recreational_estimated_benefit_balance_for_year'
                    )}
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Box className="bold">
                    {t(
                      'containers.card_order.summary_and_payment.order_summary.recreational_benefit_balance_details',
                      {
                        numberOfBeneficiaries:
                          benefitsSummary.recreational.beneficiaryNumber,
                        value: formatCurrency(
                          benefitsSummary.recreational.amount
                        )
                      }
                    )}
                  </Box>
                  <Box className="align-to-right">
                    {formatCurrency(benefitsSummary.recreational.totalAmount)}
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    {t(
                      'containers.card_order.summary_and_payment.order_summary.load_comission_for_recreational_benefit',
                      { vatPercentage: cardOrderSummary.vatPercentage }
                    )}
                  </Box>
                  <Box className="align-to-right">
                    {formatCurrency(
                      cardOrderSummary.recreationalCommissionAmount
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
          {benefitsSummary.transport.totalAmount > 0 && (
            <Box
              display="flex"
              justifyContent="space-between"
              className="body"
              borderBottom={`1px solid ${gray25}`}
              py={1}
              flex={1}
            >
              <Box className="padding-right word-break">
                {t(
                  'containers.card_order.summary_and_payment.order_summary.commuting_benefit_balance'
                )}
              </Box>
              <Box className="padding-left">
                <Box display="flex" justifyContent="space-between">
                  {t(
                    'containers.card_order.summary_and_payment.order_summary.transport_estimated_benefit_balance_for_year'
                  )}
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Box className="bold">
                    {t(
                      'containers.card_order.summary_and_payment.order_summary.commuting_benefit_balance_details',
                      {
                        numberOfBeneficiaries:
                          benefitsSummary.transport.beneficiaryNumber,
                        value: formatCurrency(benefitsSummary.transport.amount)
                      }
                    )}
                  </Box>
                  <Box className="align-to-right">
                    {formatCurrency(benefitsSummary.transport.totalAmount)}
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    {t(
                      'containers.card_order.summary_and_payment.order_summary.load_comission_for_commuting_benefit',
                      { vatPercentage: cardOrderSummary.vatPercentage }
                    )}
                  </Box>
                  <Box className="align-to-right">
                    {formatCurrency(cardOrderSummary.transportCommissionAmount)}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
          <Box
            display="flex"
            justifyContent="space-between"
            className="body"
            borderBottom={`1px solid ${gray25}`}
            py={1}
            flex={1}
          >
            <Box>
              {showCardPrice && (
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    <Box display="flex" justifyContent="space-between">
                      <Box className="padding-right">
                        {t(
                          'containers.card_order.summary_and_payment.order_summary.edenred_card'
                        )}
                      </Box>
                      <Box className="padding-left">
                        <Box display="flex" justifyContent="space-between">
                          <Box className="bold">
                            {t(
                              'containers.card_order.summary_and_payment.order_summary.edenred_card_details',
                              {
                                numberOfBeneficiaries:
                                  benefitsSummary.transport.beneficiaryNumber,
                                cardCost: formatCurrency(
                                  cardOrderSummary.duoCardFee
                                ),
                                vatPercentage: cardOrderSummary.vatPercentage
                              }
                            )}
                          </Box>
                          <Box className="align-to-right">
                            {formatCurrency(cardOrderSummary.duoCardsCosts)}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Box className="description">
                        {t(
                          'containers.card_order.summary_and_payment.order_summary.edenred_card_description'
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
              {cardOrderSummary.transportCardsCosts > 0 && (
                <Box display="flex" justifyContent="space-between">
                  <Box className="padding-right">
                    {t(
                      'containers.card_order.summary_and_payment.order_summary.transport_card'
                    )}
                  </Box>
                  <Box className="padding-left">
                    <Box display="flex" justifyContent="space-between">
                      <Box className="bold">
                        {t(
                          'containers.card_order.summary_and_payment.order_summary.transport_card_details',
                          {
                            numberOfBeneficiaries:
                              benefitsSummary.transport.beneficiaryNumber,
                            cardCost: formatCurrency(
                              cardOrderSummary.transportCardFee
                            ),
                            vatPercentage: cardOrderSummary.vatPercentage
                          }
                        )}
                      </Box>
                      <Box className="align-to-right">
                        {formatCurrency(cardOrderSummary.transportCardsCosts)}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between" flex={1}>
                <Box display="flex" flexDirection="column" flex={1}>
                  <Box display="flex" justifyContent="space-between" flex={1}>
                    <Box>
                      {t(
                        'containers.card_order.summary_and_payment.order_summary.handling_fee'
                      )}
                    </Box>
                    <Box>
                      <Box display="flex" justifyContent="space-between">
                        <Box className="align-to-right">
                          {formatCurrency(cardOrderSummary.totalDeliveryFee)}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Box className="description">
                      <Trans
                        defaults={t(
                          'containers.card_order.summary_and_payment.order_summary.handling_fee_description'
                        )}
                        components={{
                          a: (
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={t(
                                'containers.card_order.summary_and_payment.order_summary.handling_fee_description_url'
                              )}
                            />
                          )
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            className="body"
            borderBottom={`1px solid ${gray25}`}
            py={1}
            flex={1}
          >
            <Box flex={1}>
              <Box display="flex" justifyContent="space-between">
                <Box className="padding-right">
                  {t(
                    'containers.card_order.summary_and_payment.order_summary.balance_for_benefits'
                  )}
                </Box>
                <Box className="align-to-right">
                  {formatCurrency(balanceForBenefits)}
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Box className="padding-right">
                  {t(
                    'containers.card_order.summary_and_payment.order_summary.taxable_base_value'
                  )}
                </Box>
                <Box className="align-to-right">
                  {formatCurrency(taxableBaseValue)}
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Box>
                  <Box display="flex" justifyContent="space-between">
                    <Box className="padding-right">
                      <span>
                        {t(
                          'containers.card_order.summary_and_payment.order_summary.vat',
                          { vat: cardOrderSummary.vatPercentage }
                        )}
                      </span>
                      <Tooltip
                        arrow
                        placement="top"
                        id={`credit-card-tooltip`}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: '500px'
                            }
                          }
                        }}
                        title={t(
                          'containers.card_order.summary_and_payment.order_summary.vat_tooltip',
                          { vat: cardOrderSummary.vatPercentage }
                        )}
                      >
                        <Box id={`credit-card}-info-icon`}>
                          <InfoIcon color={primaryFrontColor} />
                        </Box>
                      </Tooltip>
                    </Box>
                    <Box className="align-to-right">
                      {formatCurrency(cardOrderSummary.feesVat)}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            className="body bold"
            py={1}
          >
            <Box className="padding-right">
              {t(
                'containers.card_order.summary_and_payment.order_summary.order_total'
              )}
            </Box>
            <Box className="align-to-right">
              {formatCurrency(cardOrderSummary.orderTotalWithVat)}
            </Box>
          </Box>
        </Box>
        <Box />
      </Box>
    </Box>
  );
};

const OrderSummaryWithI18n = withTranslation()(OrderSummary);
export { OrderSummaryWithI18n as OrderSummary };
